import React, { useState, useEffect } from 'react';
import { Cookie, Shield, BarChart3, Settings2, Target, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import {
  getConsent,
  hasConsented,
  acceptAll,
  rejectAll,
  setConsent,
} from '@/lib/cookieConsent';

const categories = [
  {
    key: 'necessary',
    label: 'Strictly Necessary',
    icon: Shield,
    desc: 'Essential for the site to work — routing, security, form submissions. Cannot be disabled.',
    required: true,
  },
  {
    key: 'analytical',
    label: 'Analytical & Performance',
    icon: BarChart3,
    desc: 'Helps us understand which pages visitors read so we can improve the site (e.g. PostHog analytics).',
  },
  {
    key: 'functionality',
    label: 'Functionality',
    icon: Settings2,
    desc: 'Remembers preferences you set (e.g. dismissing this banner, text size, form drafts).',
  },
  {
    key: 'targeting',
    label: 'Targeting & Advertising',
    icon: Target,
    desc: 'We do not currently use targeting cookies. This toggle is listed for transparency.',
  },
];

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytical: false, functionality: false, targeting: false });

  useEffect(() => {
    // Show if the user hasn't consented yet
    if (!hasConsented()) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const c = getConsent() || {};
      setPrefs({
        analytical: !!c.analytical,
        functionality: !!c.functionality,
        targeting: !!c.targeting,
      });
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener('cookieConsent:openPreferences', handler);
    return () => window.removeEventListener('cookieConsent:openPreferences', handler);
  }, []);

  const finalise = () => {
    setVisible(false);
    setShowPrefs(false);
  };

  const handleAccept = () => {
    acceptAll();
    finalise();
  };
  const handleReject = () => {
    rejectAll();
    finalise();
  };
  const handleSave = () => {
    setConsent(prefs);
    finalise();
  };

  if (!visible) return null;

  return (
    <>
      {/* Simple banner */}
      {!showPrefs && (
        <div
          className="fixed inset-x-0 bottom-0 z-[80] p-4 sm:p-5 animate-in slide-in-from-bottom-5 duration-500"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-banner-title"
        >
          <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 ring-1 ring-black/5 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 p-5 sm:p-6 md:items-center">
              <div className="flex items-start gap-3 md:flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#c9a227]/15 border border-[#c9a227]/30 flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-[#c9a227]" />
                </div>
                <div>
                  <div id="cookie-banner-title" className="text-sm font-bold text-[#1e3a5f] mb-1">
                    We use cookies to give you the best experience
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Strictly necessary cookies keep the site working. We would also like to set optional
                    analytics cookies to understand how visitors use the site. You can accept all,
                    reject all (except strictly necessary), or choose your own preferences. Read more
                    in our{' '}
                    <Link to="/cookies" className="text-[#1e3a5f] font-semibold underline underline-offset-2 hover:text-[#c9a227]">
                      Cookies Policy
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-[#1e3a5f] font-semibold underline underline-offset-2 hover:text-[#c9a227]">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 md:shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setShowPrefs(true)}
                  className="rounded-full text-xs sm:text-sm"
                  data-testid="cookie-preferences-btn"
                >
                  Preferences
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  className="rounded-full text-xs sm:text-sm border-slate-300"
                  data-testid="cookie-reject-all-btn"
                >
                  Reject all
                </Button>
                <Button
                  onClick={handleAccept}
                  className="rounded-full bg-[#c9a227] hover:bg-[#d4b03a] text-[#1e3a5f] text-xs sm:text-sm font-semibold"
                  data-testid="cookie-accept-all-btn"
                >
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showPrefs && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center px-0 sm:px-4 pb-0 sm:pb-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-prefs-title"
          data-testid="cookie-preferences-modal"
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowPrefs(false)} />
          <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
            <button
              onClick={() => setShowPrefs(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-slate-700"
              aria-label="Close cookie preferences"
              data-testid="cookie-preferences-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2a4e7a] px-6 py-5 text-white">
              <div className="text-[11px] uppercase tracking-wider text-[#f0c64a] font-semibold">Cookie settings</div>
              <h2 id="cookie-prefs-title" className="text-xl sm:text-2xl font-bold leading-tight mt-1">
                Choose which cookies you allow
              </h2>
              <p className="text-white/80 text-sm mt-2">
                Change these any time from the footer of any page.
              </p>
            </div>
            <div className="px-5 sm:px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {categories.map((c) => {
                const Icon = c.icon;
                const enabled = c.required ? true : !!prefs[c.key];
                return (
                  <div
                    key={c.key}
                    className="border border-slate-200 rounded-xl p-3.5 flex items-start gap-3"
                    data-testid={`cookie-cat-${c.key}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#1e3a5f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-[#1e3a5f]">{c.label}</div>
                        {c.required ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                            Always on
                          </span>
                        ) : (
                          <Switch
                            checked={enabled}
                            onCheckedChange={(v) => setPrefs({ ...prefs, [c.key]: v })}
                            data-testid={`cookie-toggle-${c.key}`}
                          />
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-2 flex flex-col sm:flex-row gap-2 border-t border-slate-100">
              <Button variant="outline" onClick={handleReject} className="flex-1 rounded-full text-sm" data-testid="cookie-prefs-reject-btn">
                Reject all
              </Button>
              <Button onClick={handleSave} className="flex-1 rounded-full bg-[#c9a227] hover:bg-[#d4b03a] text-[#1e3a5f] font-semibold text-sm" data-testid="cookie-prefs-save-btn">
                Save my choices
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
